package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.*
import com.siriusxm.pia.components.ServiceViewContext
import com.siriusxm.pia.components.serviceContentView
import com.siriusxm.pia.components.splitSelectionView
import com.siriusxm.pia.rememberCapability
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.NewProducer
import com.siriusxm.pia.views.unifiedaggregator.ProducerDetail
import com.siriusxm.pia.views.unifiedaggregator.ProducerTab
import com.siriusxm.pia.views.unifiedaggregator.navigate
import com.siriusxm.pia.views.unifiedaggregator.events.EventFilters
import unifiedcontent.aggregator.ProducerDetails

/**
 * The view for browsing and editing producers.
 * Displays a list of producers with options to filter disabled items.
 * Users authorized for ProducerCreate can add new producers via the action button.
 * A settings icon in the top-right allows toggling visibility of disabled producers.
 *
 * @param selectedId the producer to show detail for, or null to show the aggregate dashboard.
 * @param tab which of the selected producer's detail tabs to show. Comes from the URL (UCA-1028) via
 *   [com.siriusxm.pia.views.unifiedaggregator.parseProducerPath], so a shared link lands on the tab
 *   it names. Typed rather than a `String` so an unknown tab can't reach `tabView`.
 * @param filters the Events/Errors filter selections, decoded from the URL query (UCA-1028
 *   follow-up). Passed through to [Producer] so a filtered view is shareable. Unset by default, which
 *   is exactly the state a URL with no query decodes to.
 */
@Composable
fun ServiceViewContext.producers(
    aggregator: AggregatorService,
    selectedId: String? = null,
    tab: ProducerTab = ProducerTab.Default,
    filters: EventFilters = EventFilters()
) {
    val producers = aggregator.producers
    val selected = producers.find { it.id == selectedId }
    var showAll by remember { mutableStateOf(selectedId == null) }
    // Creation is uncommon, so resolve it only on the aggregate list where the action can appear.
    // Capabilities caches the resulting action-only query if this screen is revisited.
    val canCreateProducer = selectedId == null &&
            aggregator.context.rememberCapability(ProducerCreateCapability).value

    // Settings state for show disabled toggle
    var settings by remember { mutableStateOf(ProducerConsumerSettings()) }

    val allProducer = ProducerDetails("all", "All")

    LaunchedEffect(selectedId) {
        if (selectedId != null) {
            showAll = false
        }
    }

    // Reload producers when settings change
    LaunchedEffect(settings.showDisabled) {
        aggregator.app.reloadProducers(settings.showDisabled)
    }

    serviceContentView({
        title = "Producers"
        fixedHeight = true

        // Settings icon (top-right) that opens a dialog for view options
        settings {
            producerConsumerSettingsDialog(settings, "producers") { newSettings ->
                settings = newSettings
            }
        }

        if (canCreateProducer) {
            this.action {
                title = "New Producer"
                primary = true
                action {
                    aggregator.navigate(NewProducer)
                }
            }
        }
        breadcrumbs {
            crumb("Aggregator", "aggregator") {
                crumb("Producers", "producers") {
                    if (selected != null) {
                        crumb(selected.name ?: selected.id, null)
                    }
                }
            }
        }
    }) {
        splitSelectionView<ProducerDetails> {
            fixedHeight = true
            items = listOf(allProducer) + producers
            selection = if (showAll) allProducer else producers.find { it.id == selectedId }
            onSelect {
                if (it.id != "all") {
                    showAll = false
                    aggregator.navigate(ProducerDetail(it.id))
                } else {
                    showAll = true
                }
            }
            renderListItem {
                buildString {
                    append(it.name ?: it.id)
                    if (it.disabled == true) {
                        append(" (Disabled)")
                    }
                }
            }
            render { producer ->
                if (showAll) {
                    ProducersDashboard(aggregator)
                } else {
                    Producer(aggregator, producer, tab, filters)
                }
            }
        }
    }
}
